import { Dialog, DialogContent, IconButton, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import StepHeader from "./StepHeader";
import BasicInfo from "./BasicInfo";
import Supplier from "./Supplier";
import Finance from "./Finance";
import PhysicalProps from "./PhysicalProps";
import { IEquipment } from "../../../types/equipment"; 
import { FormDataType } from "../types/types";
import { tokens } from "../../../theme";

interface AddEquipmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FormDataType) => Promise<void>;
  equipment?: IEquipment | null;
  isEdit: boolean;
}

const initialFormData: FormDataType = {
  name: "",
  brand: "",
  serialNo: "",
  category: "",
  subCategory: "",
  status: "",
  model: "",
  manufacture: "",
  location: "",
  length: "",
  height: "",
  width: "",
  weight: "",
  transportVolume: "",
  measuringUnit: "",
  current: "",
  power: "",
  purchaseCost: "",
  currentValue: "",
  purchaseDate: "",
  warrantyExpiry: "",
  rentalValue: "",
  supplierName: "",
  contactName: "",
  supplierPhone: "",
  supplierEmail: "",
  supplierWebsite: "",
};

type StepType = 1 | 2 | 3 | 4;

export default function AddEquipmentDialog({
  open,
  onClose,
  onSubmit,
  equipment,
  isEdit,
}: AddEquipmentDialogProps) { 
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const [step, setStep] = useState<StepType>(1);
  const [formData, setFormData] = useState<FormDataType>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open && equipment && isEdit) {
      setFormData({ ...initialFormData, ...equipment });
      setStep(1);
    } else if (open && !isEdit) {
      setFormData(initialFormData);
      setStep(1);
    }
  }, [open, equipment, isEdit]);

  const handleChange = (field: keyof FormDataType, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => setStep((prev) => Math.min(prev + 1, 4) as StepType);
  const handlePrevious = () => setStep((prev) => Math.max(prev - 1, 1) as StepType);

  const handleSaveSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    await onSubmit(formData);
    setIsSubmitting(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 5, overflow: "hidden", maxHeight: "90vh" },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 10,
          top: 10,
          color: colors.primary[900],
          zIndex: 10,
        }}
      >
        <CloseIcon />
      </IconButton>

      <StepHeader step={step} />

      <DialogContent sx={{ pt: 3, pb: 2, px: 4 }}>
        {step === 1 && (
          <BasicInfo
            formData={formData}
            onChange={handleChange}
            onNext={handleNext}
            onSaveGear={handleSaveSubmit}
          />
        )}
        {step === 2 && (
          <PhysicalProps
            formData={formData}
            onChange={handleChange}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onSaveGear={handleSaveSubmit}
          />
        )}
        {step === 3 && (
          <Finance
            formData={formData}
            onChange={handleChange}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onSaveGear={handleSaveSubmit}
          />
        )}
        {step === 4 && (
          <Supplier
            formData={formData}
            onChange={handleChange}
            onPrevious={handlePrevious}
            onSubmit={handleSaveSubmit}
            onSaveGear={handleSaveSubmit}
            isSubmitting={isSubmitting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}